// Client-side mirror of src/utils/checkInDate.js. Keep the two in sync so
// vendors see identical strings in the UI and in their emails.

export const FESTIVAL_OPEN_HOUR = 8;
export const FESTIVAL_CLOSE_HOUR = 16;
export const FESTIVAL_HOURS_SHORT = '8am–4pm';
export const FESTIVAL_HOURS_LABEL = '8:00 AM – 4:00 PM';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday',
  'Thursday', 'Friday', 'Saturday',
];

const parseEventDate = (eventDate) => {
  if (!eventDate) return null;
  if (eventDate instanceof Date) {
    if (Number.isNaN(eventDate.getTime())) return null;
    return new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate());
  }
  const str = String(eventDate);
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const ordinalSuffix = (day) => {
  const mod100 = day % 100;
  if (mod100 >= 11 && mod100 <= 13) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

const formatWeekdayMonthDay = (date) => {
  if (!date) return '';
  const weekday = WEEKDAYS[date.getDay()];
  const month = MONTHS[date.getMonth()];
  const day = date.getDate();
  return `${weekday}, ${month} ${day}${ordinalSuffix(day)}`;
};

export const formatEventDateLabel = (eventDate) => {
  const date = parseEventDate(eventDate);
  if (!date) return '';
  return `${formatWeekdayMonthDay(date)}, ${date.getFullYear()}`;
};

export const formatCheckInDateLabel = (eventDate, checkInDate) => {
  if (!checkInDate) return 'Not specified';
  const saturday = parseEventDate(eventDate);
  if (!saturday) {
    return checkInDate === 'friday'
      ? 'Friday after 12pm'
      : checkInDate === 'saturday'
        ? 'Saturday at 6am'
        : 'Not specified';
  }
  const target = new Date(saturday);
  if (checkInDate === 'friday') {
    target.setDate(saturday.getDate() - 1);
    return `${formatWeekdayMonthDay(target)} after 12pm`;
  }
  if (checkInDate === 'saturday') {
    return `${formatWeekdayMonthDay(target)} at 6am`;
  }
  return 'Not specified';
};

export const formatSetupWindowLabels = (eventDate) => {
  const saturday = parseEventDate(eventDate);
  if (!saturday) {
    return {
      fridaySetupLabel: 'Friday: 12:00 PM - 6:00 PM',
      saturdaySetupLabel: 'Saturday: 6:00 AM - 8:00 AM',
    };
  }
  const friday = new Date(saturday);
  friday.setDate(saturday.getDate() - 1);
  return {
    fridaySetupLabel: `${formatWeekdayMonthDay(friday)}: 12:00 PM - 6:00 PM`,
    saturdaySetupLabel: `${formatWeekdayMonthDay(saturday)}: 6:00 AM - 8:00 AM`,
  };
};

// Pin a festival event_date (YYYY-MM-DD) to local opening time for countdowns.
export const festivalDateAtOpen = (eventDate) => {
  const date = parseEventDate(eventDate);
  if (!date) return null;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    FESTIVAL_OPEN_HOUR,
    0,
    0
  );
};

// Compute the second Saturday in November for the given year. Used as a
// fallback for public pages when the active-festival fetch fails.
export const secondSaturdayOfNovember = (year) => {
  const firstOfNov = new Date(year, 10, 1);
  const firstSaturdayOffset = (6 - firstOfNov.getDay() + 7) % 7;
  const day = 1 + firstSaturdayOffset + 7;
  return new Date(year, 10, day, FESTIVAL_OPEN_HOUR, 0, 0);
};
